import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { DomainCreateSchema, DomainMutationSchema } from './schemas';
import type { DomainQueryAllSchema, DomainQuerySchema } from './schemas/domain-query-schema';

export function getDomainsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['DOMAINS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<DomainQueryAllSchema>({
        endpoint: '/api/v1/domains',
        params: `${apiParamStr}&include=registrar,infrastructure,created_by`,
      });

      return res;
    },
  };
}

export function useGetDomains(apiParamStr: string) {
  return useSuspenseQuery(getDomainsQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['DOMAINS'] });
};

export function useGetDomain(id: string) {
  return useSuspenseQuery(getDomainQuery(id));
}

export function getDomainQuery(id: string) {
  return {
    queryKey: ['DOMAIN', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: DomainQuerySchema }>({
        endpoint: `/api/v1/domains/${id}`,
        params: `include=registrar,infrastructure,client,application`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateDomainQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['DOMAIN', id] });
};

export const useGetDomainsMutation: UseControlledMutation<DomainQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['DOMAINS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<DomainQueryAllSchema>({
            endpoint: '/api/v1/domains',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: DomainQuerySchema }, unknown, DomainCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/domains/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: DomainQuerySchema }, ApiError, DomainMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/domains/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'domains', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: DomainQuerySchema }, ApiError, Partial<DomainMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/domains/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['DOMAIN_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/domains/${id}`,
        method: 'DELETE',
      }),
  });
